import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { BASE_URL } from '../config/api'; // Assuming BASE_URL is correctly defined

const EditCoupon = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        code: '',
        valid_up_to: '',
        calculation: 'percentage',
        value: '',
        max_amount: '',
        use_type: 'single',
    });
    const [loading, setLoading] = useState(false);

    // Fetch the coupon data to edit
    useEffect(() => {
        fetch(`${BASE_URL}coupons/${id}`)
            .then((response) => response.json())
            .then((data) => {
                if (data.status === 'success') {
                    setFormData({
                        code: data.data.code,
                        valid_up_to: data.data.valid_up_to.split('T')[0], // Format the date
                        calculation: data.data.calculation,
                        value: data.data.value,
                        max_amount: data.data.max_amount,
                        use_type: data.data.use_type,
                    });
                } else {
                    alert('Failed to fetch coupon details.');
                    navigate('/CouponList'); // Redirect if unable to fetch details
                }
            })
            .catch((error) => {
                console.error('Error fetching coupon data:', error);
                alert('An error occurred while fetching coupon details.');
                navigate('/CouponList');
            });
    }, [id, navigate]);

    // Handle form input changes
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prevData) => ({ ...prevData, [name]: value }));
    };

    // Handle form submission
    const handleSubmit = (e) => {
        e.preventDefault();
        setLoading(true);

        fetch(`${BASE_URL}coupons/${id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(formData),
        })
            .then((response) => response.json())
            .then((data) => {
                setLoading(false);
                if (data.status === 'success') {
                    alert('Coupon updated successfully!');
                    navigate('/CouponList'); // Navigate to Coupon List page after successful update
                } else {
                    alert('Failed to update coupon. Please try again.');
                }
            })
            .catch((error) => {
                setLoading(false);
                console.error('Error updating coupon:', error);
                alert('An error occurred. Please try again.');
            });
    };

    return (
        <div id="main">
            <header className="mb-3">
                <a href="#" className="burger-btn d-block d-xl-none">
                    <i className="bi bi-justify fs-3"></i>
                </a>
            </header>

            <div className="page-heading">
                <div className="page-title">
                    <div className="row">
                        <div className="col-12 col-md-6 order-md-1 order-last">
                            <h3>Edit Coupon</h3>
                        </div>
                        <div className="col-12 col-md-6 order-md-2 order-first">
                            <nav aria-label="breadcrumb" className="breadcrumb-header float-start float-lg-end">
                                <ol className="breadcrumb"></ol>
                            </nav>
                        </div>
                    </div>
                </div>
            </div>

            {/* Form Section */}
            <section className="section">
                <div className="card">
                    <div className="card-header">
                        <h4 className="card-title">Coupon Details</h4>
                    </div>
                    <div className="card-body">
                        <form onSubmit={handleSubmit}>
                            <div className="row">
                                <div className="col-md-6 mb-3">
                                    <label htmlFor="code" className="form-label">Code</label>
                                    <input
                                        type="text"
                                        id="code"
                                        name="code"
                                        className="form-control"
                                        value={formData.code}
                                        onChange={handleChange}
                                        required
                                    />
                                </div>

                                <div className="col-md-6 mb-3">
                                    <label htmlFor="valid_up_to" className="form-label">Valid Up To</label>
                                    <input
                                        type="date"
                                        id="valid_up_to"
                                        name="valid_up_to"
                                        className="form-control"
                                        value={formData.valid_up_to}
                                        onChange={handleChange}
                                        required
                                    />
                                </div>

                                <div className="col-md-6 mb-3">
                                    <label htmlFor="calculation" className="form-label">Calculation</label>
                                    <select
                                        id="calculation"
                                        name="calculation"
                                        className="form-select"
                                        value={formData.calculation}
                                        onChange={handleChange}
                                        required
                                    >
                                        <option value="percentage">Percentage</option>
                                        <option value="flat">Flat</option>
                                    </select>
                                </div>

                                <div className="col-md-6 mb-3">
                                    <label htmlFor="value" className="form-label">Value</label>
                                    <input
                                        type="number"
                                        id="value"
                                        name="value"
                                        className="form-control"
                                        value={formData.value}
                                        onChange={handleChange}
                                        required
                                    />
                                </div>

                                <div className="col-md-6 mb-3">
                                    <label htmlFor="max_amount" className="form-label">Max Amount</label>
                                    <input
                                        type="number"
                                        id="max_amount"
                                        name="max_amount"
                                        className="form-control"
                                        value={formData.max_amount}
                                        onChange={handleChange}
                                        required
                                    />
                                </div>

                                <div className="col-md-6 mb-3">
                                    <label htmlFor="use_type" className="form-label">Use Type</label>
                                    <select
                                        id="use_type"
                                        name="use_type"
                                        className="form-select"
                                        value={formData.use_type}
                                        onChange={handleChange}
                                        required
                                    >
                                        <option value="single">Single</option>
                                        <option value="multiple">Multiple</option>
                                    </select>
                                </div>
                            </div>

                            <div className="text-end">
                                <button type="submit" className="btn btn-primary" disabled={loading}>
                                    {loading ? 'Updating...' : 'Update Coupon'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default EditCoupon;
